import React from "react";
import { AGENT_READ_ONLY_MESSAGE } from "../hooks/useAccountConfig";

interface FilterOptions {
  areaCodes: string[];
  states: string[];
}

interface PhoneGridToolbarProps {
  searchString: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  filterBy: string;
  onFilterByChange: (value: string) => void;
  filterValue: string;
  onFilterValueChange: (value: string) => void;
  ftcFilterDays: string;
  onFtcFilterDaysChange: (value: string) => void;
  onResetFilter: () => void;
  filterOptions: FilterOptions;
  /** Remediation (attr 104) or Remediation Daily Scans (attr 106) plan; undefined while loading. */
  hasRemediationPlan: boolean | undefined;
  selectedCount: number;
  isSysadmin: boolean;
  isTrustCallV3: boolean;
  /** DNC-390: trustcall_write permission — write actions render disabled without it. */
  canWrite: boolean;
  onDelete: () => void;
  onRescan: () => void;
  onAddTnrm: () => void;
  onRemoveTnrm: () => void;
  onOriginateCall: () => void;
  onExportCsv: () => void;
  onLearnRemediation: () => void;
  isLearnRemediationDisabled: boolean;
}

const FILTER_OPTIONS = [
  { value: "", label: "-Choose Filter-" },
  { value: "appscore", label: "App Score" },
  { value: "areacode", label: "Areacode" },
  { value: "state", label: "State" },
  { value: "scamscore", label: "Spam Score" },
  { value: "ftcfilter", label: "FTC Complaints" },
];

// DB stores "Clean" (not "None") — MVC dropdown had value="None" but that was
// a pre-existing bug (Kendo column checkbox filter worked because it filtered
// client-side against the already-mapped DTO). The server-side filter at
// ScamScoreDomain line 1298 matches against raw DB values.
const SPAM_SCORE_OPTIONS = [
  { value: "", label: "-Choose SpamScore-" },
  { value: "High", label: "High" },
  { value: "Medium", label: "Medium" },
  { value: "Clean", label: "Clean" },
];

const APP_SCORE_OPTIONS = [
  { value: "", label: "-Choose AppScore-" },
  { value: "Flagged", label: "Flagged" },
  { value: "Clean", label: "Clean" },
];

const FTC_COMPLAINT_OPTIONS = [
  { value: "", label: "-Choose FTC Complaints-" },
  { value: "HasComplaints", label: "Has Complaints" },
  { value: "NoComplaints", label: "No Complaints" },
];

const PhoneGridToolbar: React.FC<PhoneGridToolbarProps> = ({
  searchString,
  onSearchChange,
  onSearch,
  filterBy,
  onFilterByChange,
  filterValue,
  onFilterValueChange,
  onResetFilter,
  filterOptions,
  hasRemediationPlan,
  selectedCount,
  isSysadmin,
  isTrustCallV3,
  canWrite,
  onDelete,
  onRescan,
  onAddTnrm,
  onRemoveTnrm,
  onOriginateCall,
  onExportCsv,
  onLearnRemediation,
  isLearnRemediationDisabled,
}) => {
  const hasSelection = selectedCount > 0;
  const isSingleSelection = selectedCount === 1;
  const showSysadminActions = isSysadmin && isTrustCallV3;
  // DNC-390: spread onto every write button so a user without trustcall_write sees
  // the action but cannot fire it. The banner explains why; this is the tooltip.
  const writeGuard = {
    disabled: !canWrite,
    title: !canWrite ? AGENT_READ_ONLY_MESSAGE : undefined,
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") onSearch();
  };

  // Determine which sub-filter dropdown to show based on main filter selection
  const renderSubFilter = () => {
    switch (filterBy) {
      case "areacode":
        return (
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 180 }}
            value={filterValue}
            onChange={(e) => onFilterValueChange(e.target.value)}
          >
            <option value="">-Choose AreaCode-</option>
            {filterOptions.areaCodes.map((code) => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
        );
      case "state":
        return (
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 180 }}
            value={filterValue}
            onChange={(e) => onFilterValueChange(e.target.value)}
          >
            <option value="">-Choose State-</option>
            {filterOptions.states.map((state) => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        );
      case "scamscore":
        return (
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 180 }}
            value={filterValue}
            onChange={(e) => onFilterValueChange(e.target.value)}
          >
            {SPAM_SCORE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      case "appscore":
        return (
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 180 }}
            value={filterValue}
            onChange={(e) => onFilterValueChange(e.target.value)}
          >
            {APP_SCORE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      case "ftcfilter":
        return (
          <>
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: 200 }}
              value={filterValue}
              onChange={(e) => onFilterValueChange(e.target.value)}
            >
              {FTC_COMPLAINT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </>
        );
      default:
        return null;
    }
  };

  const hasActiveFilter = searchString || filterBy;

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      {/* Search */}
      <div className="input-group" style={{ maxWidth: 280 }}>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Search phone number..."
          value={searchString}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={onSearch}
        >
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 512 512"><path d="M365.4 332.3c26.1-34.2 41.6-76.9 41.6-123.3C407 93.8 313.2 0 198 0S-11 93.8-11 209s93.8 209 209 209c46.4 0 89.1-15.5 123.3-41.6L451.7 506.8c7.6 7.6 20 7.6 27.6 0l13.6-13.6c7.6-7.6 7.6-20 0-27.6L365.4 332.3zM198 374c-91.2 0-165-73.8-165-165S106.8 44 198 44s165 73.8 165 165-73.8 165-165 165z"/></svg>
        </button>
      </div>

      {/* Main filter dropdown */}
      <select
        className="form-select form-select-sm"
        style={{ maxWidth: 180 }}
        value={filterBy}
        onChange={(e) => onFilterByChange(e.target.value)}
      >
        {FILTER_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Sub-filter dropdown (shown based on main filter selection) */}
      {renderSubFilter()}

      {/* Reset Filter — visible when search or filter is active (matches MVC #resetFilter) */}
      {hasActiveFilter && (
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={onResetFilter}
        >
          Reset Filter
        </button>
      )}

      {/* Learn About Remediation (no remediation plan) — matches MVC #learnAboutRemediation */}
      {hasRemediationPlan === false && (
        <button
          className="btn btn-success btn-sm"
          onClick={onLearnRemediation}
          {...writeGuard}
          disabled={isLearnRemediationDisabled || !canWrite}
        >
          Learn About Remediation
        </button>
      )}

      {/* Spacer */}
      <div className="flex-grow-1" />

      {/* Bulk actions (visible when rows selected) */}
      {hasSelection && (
        <>
          <button className="btn btn-danger btn-sm" onClick={onDelete} {...writeGuard}>
            Delete ({selectedCount})
          </button>
          {showSysadminActions && (
            <>
              <button className="btn btn-primary btn-sm" onClick={onRescan} {...writeGuard}>
                Rescan
              </button>
              <button className="btn btn-primary btn-sm" onClick={onAddTnrm} {...writeGuard}>
                Add TNRM
              </button>
              <button className="btn btn-danger btn-sm" onClick={onRemoveTnrm} {...writeGuard}>
                Remove TNRM
              </button>
            </>
          )}
          {showSysadminActions && isSingleSelection && (
            <button className="btn btn-success btn-sm" onClick={onOriginateCall} {...writeGuard}>
              Originate Call
            </button>
          )}
        </>
      )}

      {/* Export — always visible (matching MVC toolbar) */}
      <button className="btn btn-outline-secondary btn-sm" onClick={onExportCsv}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ marginRight: 4, verticalAlign: "text-bottom" }}>
          <path d="M20.707 10.38v9.72a1.416 1.416 0 0 1-1.417 1.417H4.71A1.416 1.416 0 0 1 3.293 20.1v-9.72A1.416 1.416 0 0 1 4.71 8.963h2.43a.607.607 0 0 1 0 1.214H4.71a.204.204 0 0 0-.203.203v9.72a.204.204 0 0 0 .203.203h14.58a.204.204 0 0 0 .203-.203v-9.72a.204.204 0 0 0-.203-.203h-2.43a.608.608 0 0 1 0-1.215h2.43a1.416 1.416 0 0 1 1.417 1.417zM8.378 5.95l3.014-3.012v9.872a.608.608 0 0 0 1.215 0V2.938L15.62 5.95a.607.607 0 1 0 .86-.86l-4.05-4.05a.607.607 0 0 0-.86 0L7.52 5.09a.607.607 0 1 0 .86.86z" />
        </svg>
        Export All CSV
      </button>
    </div>
  );
};

export default PhoneGridToolbar;
